<template>
  <div
    class="sidebar"
    :class="{ open: appStore.sidebarIsOpen, mobile: isMobile }"
    @mouseenter="appStore.toggleSidebar(true)"
    @mouseleave="appStore.toggleSidebar(false)"
  >
    <div class="sidebar-header">
      <LogoSimple />
    </div>
    <transition name="fade" mode="out-in">
      <div class="sidebar-body" :key="companyStore.currentCompany?.company_id">
        <div class="items-container">
          <Item
            :label="{ text: 'Nouvelle campagne', pluralization: 0 }"
            icon="bi bi-pencil-square"
            :routeParams="{ name: 'newCampaign' }"
            :animationIsFinished="state.animationIsFinished"
            :sidebarIsOpen="appStore.sidebarIsOpen"
            data-step="6"
          />
        </div>
        <div class="items-container">
          <small class="menu-title">MENU</small>
          <Item
            v-for="(item, index) in items"
            :key="index"
            :label="item.label"
            :icon="item.icon"
            :routeParams="item.route"
            :animationIsFinished="state.animationIsFinished"
            :sidebarIsOpen="appStore.sidebarIsOpen"
            :data-step="item.dataStep"
          />
        </div>
        <div v-if="spaceStore.companySpaces" class="items-container">
          <small class="menu-title">ESPACES</small>
          <Item
            :label="{ text: 'Gérer mes espaces', pluralization: 0 }"
            icon="bi bi-folder"
            :routeParams="{ name: 'spaces' }"
            :animationIsFinished="state.animationIsFinished"
            :sidebarIsOpen="appStore.sidebarIsOpen"
            data-step="3"
          />
          <div class="divider" />
          <ItemFetch
            v-for="(item, index) in [...spaceStore.companySpaces].sort((a, b) =>
              a.name.localeCompare(b.name)
            )"
            :key="index"
            :space="item"
            :animationIsFinished="state.animationIsFinished"
            :sidebarIsOpen="appStore.sidebarIsOpen"
          />
        </div>
      </div>
    </transition>
    <div class="sidebar-body-addons">
      <div class="items-container" data-step="4">
        <small class="menu-title">CREDITS</small>
        <div class="credit-wrapper" @click="appStore.openPaymentModal('sidebar')">
          <div class="credits-container">
            <div class="tag red" v-text="labelCredit" />
            <img src="/images/defaults/space/lemon-yellow.svg" alt="Credits" />
          </div>
          <div class="credits-info">
            <div
              class="total"
              v-text="companyStore.currentCompany?.credits?.available"
            />
            Crédit{{
              companyStore.currentCompany?.credits?.available > 1 ? "s" : ""
            }}
            restant
          </div>
        </div>
      </div>
    </div>
    <Footer
      :class="{ open: appStore.sidebarFooterIsOpen || isMobile }"
      @mouseenter="!isMobile ? appStore.toggleSidebarFooter(true) : null"
      @mouseleave="!isMobile ? appStore.toggleSidebarFooter(false) : null"
      :animationIsFinished="state.animationIsFinished"
      :sidebarIsOpen="appStore.sidebarIsOpen"
      data-step="5"
    />
  </div>
</template>

<script setup>
import { reactive, watch, computed, onMounted } from "vue";
import { useCompanyStore } from "@/stores/company.js";
import { useSpaceStore } from "@/stores/space.js";
import { useAppStore } from "@/stores/app.js";
import { useUserStore } from "@/stores/user.js";
import Footer from "./Footer.vue";
import Item from "./Item.vue";
import ItemFetch from "./ItemFetch.vue";

const companyStore = useCompanyStore();

const spaceStore = useSpaceStore();

const appStore = useAppStore();

const { hasPermission } = useUserStore();

defineProps({
  isMobile: {
    type: Boolean,
    required: true,
  },
});

const state = reactive({
  animationIsFinished: false,
});

const labelCredit = computed(() => {
  if (!companyStore.currentCompany?.credits) return null;

  return companyStore.currentCompany.credits.available > 999
    ? `${Math.floor(companyStore.currentCompany.credits.available / 1000)}K+`
    : companyStore.currentCompany.credits.available;
});

const items = computed(() => {
  if (!companyStore.currentCompany) return null;

  const items_list = [
    {
      label: {
        text: "home.label",
        pluralization: 1,
      },
      icon: "bi bi-house",
      route: {
        name: "home",
        params: {
          companyId: companyStore.currentCompany.company_id,
        },
      },
    },
  ];

  if (hasPermission("billingHistory", 1) || hasPermission("members", 1)) {
    items_list.push({
      label: {
        text: "company.label",
        pluralization: 1,
      },
      icon: "bi bi-wallet2",
      route: {
        name: "company",
      },
      dataStep: "2",
    });
  }

  return items_list;
});

watch(
  () => companyStore.currentCompany?.company_id,
  async () => {
    if (
      companyStore.currentCompany?.company_id &&
      spaceStore.companySpaces.length === 0
    ) {
      await spaceStore.getSpaces(companyStore.currentCompany.company_id);
    }
  },
  { immediate: true }
);

watch(
  () => appStore.sidebarIsOpen,
  (isOpen) => {
    if (!isOpen) {
      state.animationIsFinished = false;
    } else {
      setTimeout(() => {
        state.animationIsFinished = true;
      }, 150);
    }
  }
);

onMounted(() => {
  appStore.sidebarIsMounted = true;
  state.animationIsFinished = true;
});
</script>
